import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { Button, CheckBox, CheckStyles, SECTION_BASIC, NewModal, MainContentPaddingTypes, NewSelect, NewInput, NewInputListHandles } from "../..";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { useParams } from "react-router";
import { SectionBasicTypes } from "../Section/sectionBasic";
import { LfSectionsInfo } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";
import { NewFieldStyles } from "../../../Definitions";
import { useTerminals } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useTerminals";
import { useSelectOptions } from "../../_Components";

interface EmptySection_Props{
  isModalOpen: boolean
  onClose: () => void,
  system: number
}

export default function EmptySectionModal({isModalOpen, onClose, system}:EmptySection_Props) {
  const { t } = useTranslation();
  const [activePenId, setActivePenId] = useState<number[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [timeChange, setTimeChange] = useState(false);
  const [timeChangeValue, setTimeChangeValue] = useState<number>(10);
  const [message, setMessage] = useState<string>("")
  const [isSucces, setIsSuccess] = useState<boolean>()
  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const systemId = urlSystemId ? +urlSystemId : system;
  const {sectionOptions} = useSelectOptions({ systemId: systemId })
  const mobile = useMediaQuery(`(max-width: 650px)`);

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { doEmptyFeed, data: terminals } = useTerminals(connection) 
  const { call: getLfSectionsInfo, data: lfSectionsState } = useSection(connection);
  const sections = useMemo( () => (lfSectionsState[domain] ? lfSectionsState[domain].data.filter(section => section.system_id === systemId) : undefined), [domain, lfSectionsState, systemId] );
  const [activeSection, setActiveSection] = useState< LfSectionsInfo | undefined >(sections !== undefined ? sections[0] : undefined);
  const numberOfPens = activeSection?.section_locations.length;

  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setActiveSection(sections !== undefined ? sections[0] : undefined);
  }, [sections]);

  const allPenIds = activeSection&&activeSection.section_locations.map(
    (pen) => pen.location_id
  );

  const penTerminals = terminals[domain]?.data.filter((terminal) => terminal.fk_locations ? activePenId.includes(terminal.fk_locations) : false )

  useEffect(() => {
    if (isModalOpen && activeSection) {
      setActivePenId(allPenIds ?? []);
      setIsAllSelected(true);
      setTimeChange(false);
    }
  }, [activeSection, isModalOpen]);

  useEffect(() => {
    setIsAllSelected(activePenId.length === numberOfPens);
  }, [activePenId, numberOfPens]);

  function handlePenSelected(penId: number) {
    setActivePenId((prev) => {
      if (!prev) return [penId];
      return prev.includes(penId)
        ? prev.filter((id) => id !== penId)
        : [...prev, penId];
    });
  }

  if (!sections) return null;
  const currentIndex = sections.findIndex(
    (s) => s.section_id === activeSection?.section_id
  );

  const handleEmptyFeed = async () => {
    if (activePenId) {
      await Promise.all(
        penTerminals.map((async (terminal, index) => { 
          return await doEmptyFeed(terminal.terminal_id, 30, 2, 2*10 /* -triggerAmount- (to min) */);
        }))
      ).then((e) => {
        const success = !e.some(res => res === false);
        if (success === true) {
          setMessage(t("success", {defaultValue: "Success"}));
          setIsSuccess(true)
        } else {
          setMessage(t("systemControls.general.emptyingFailed", { defaultValue: "Emptying failed" }))
          setIsSuccess(false)
        }
        setTimeout(() => {
          setIsSuccess(undefined)
          setMessage("");
        }, 5000);
      })
    }
  }

  const handleEmptyFeedWithTimeChange = async () => {
    if (activePenId) {
      await Promise.all(
        penTerminals.map((async (terminal, index) => { 
          return await doEmptyFeed(terminal.terminal_id, 30, 2, 2*timeChangeValue);
        }))
      ).then((e) => {
        const success = !e.some(res => res === false);
        if (success === true) {
          setMessage(t("success", {defaultValue: "Success"}));
          setIsSuccess(true)
        } else {
          setMessage(t("systemControls.general.emptyingFailed", { defaultValue: "Emptying failed" }))
          setIsSuccess(false)
        }
        setTimeout(() => {
          setIsSuccess(undefined)
          setMessage("");
        }, 5000);
      })
    }
  }

  const handleOnClick = () => {
    if (timeChange) {
      handleEmptyFeedWithTimeChange();
    } else {
      handleEmptyFeed();
    }
  };

  return (
    activeSection && (
      <NewModal
        header={t("systemControls.general.systemControls", {
          defaultValue: "System Controls",
        })}
        modalState={isModalOpen}
        predefinedButtons={{
          onClose: () => { setActivePenId(allPenIds ?? []); setActiveSection(sections[0]); onClose() },
          onNext: () => {setActiveSection(sections[(currentIndex + 1) % sections.length])},
          onPrev: () => {setActiveSection(sections[(currentIndex - 1 + sections.length) % sections.length])},
        }}
        subHeader={activeSection?.name}
        overrides={{
          backgroundColor: colors.greyLightTertiary,
          mainContentPadding: MainContentPaddingTypes.primary,
        }}
        headerContent={
          <NewSelect
            options={sectionOptions ?? []}
            key={activeSection.section_id}
            onChange={(i) => setActiveSection(sections.find(section => section.section_id === i?.id))}
            valueOverrides={{
              defaultValue: sectionOptions?.find(option => option.id === activeSection.section_id)
            }}
            fieldOverrides={{
              fieldWidth: mobile ? "100px" : "160px"
            }}
          />
        }
        mainContent={
          <div
          style={{ width: mobile ? "300px" : "600px", boxSizing: "border-box", gap: "20px", display: "grid", gridTemplateColumns: mobile ? "1fr" : "270px 1fr", gridTemplateRows: mobile ? "fit-content(100%) fit-content(100%)" : "1fr", margin: "0 auto"}}>
            {/* Controls */}
            <div
              style={{ gridArea: mobile ? "2/1/3/2" : "1/1/2/2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: "15px"}}>
              <Button
                content={t("systemControls.general.emptySelected", { defaultValue: "Empty Selected" })}
                startEnhancer={glyphs.Start({ size: "30px" })}
                overrides={{
                  baseButton: { backgroundColor: colors.greySecondary },
                  hoverStyles: { backgroundColor: colors.greyDarkSecondary },
                  startEnhancerStyles: { ...css.button_PositionStartEnhancerStart},
                }}
                onClick={handleOnClick}
              />
              {/* {message} */}
              <div
                style={{ width: "100%", height: "30px", display: "flex", alignItems: "center", gap: "20px"}}>
                <CheckBox
                  checkStyle={CheckStyles.grey}
                  onChange={() => {setTimeChange(!timeChange); if (timeChangeValue !== 10) setTimeChangeValue(10)}}
                  isChecked={timeChange}
                  text={t("systemControls.general.changeTime", { defaultValue: "Change Time" })}
                />
                  {timeChange ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "5px"}}>
                    <NewInput
                      placeholder={t("units.min", { defaultValue: "min" })}
                      onChange={(e) => setTimeChangeValue(Number(e))}
                      fieldOverrides={{
                        fieldStyle: NewFieldStyles.greyTertiary,
                        fieldWidth: "100px",
                      }}
                      valueOverrides={{
                        valueType: "number",
                        defaultValue: timeChangeValue
                      }}
                    />
                    <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}> {t("units.min", { defaultValue: "min" })} </p>
                  </div>
                ) : (
                  <></>
                )}
              </div>

              <div style={{ height: "2px", width: "100%", backgroundColor: colors.greyTertiary }} />
              {/* Selected pens */}
              <div style={{ width: "100%", maxHeight: "200px", overflowX: "hidden", overflowY: "scroll", scrollbarWidth: "thin", scrollbarColor: colors.greyTertiary + colors.transparent, display: "flex", flexDirection: "column", gap: "5px"}}>
                {activePenId.length === 0 ? (
                  <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, width: "100%", textAlign: "center"}}> {`${t("systemControls.general.noPensSelected", { defaultValue: "No pens selected" })}!`} </p>
                ) : (
                  <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyDarkPrimary, width: "100%" }}> {timeChange ? `${t("systemControls.general.emptyingFor", { defaultValue: "Emptying for" })} ${timeChangeValue} ${t("units.min", { defaultValue: "min" })}:` : `${t("systemControls.general.emptyingFor", { defaultValue: "Emptying for" })} 10 ${t("units.min", { defaultValue: "min" })}:`} </p>
                )}
                <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
                  {activePenId
                    .map(penId => `${t("locationFeederPages.locationFeeder.pen", {defaultValue: "Pen"})} ${sections?.flatMap(section => section.section_locations).find(location => location.location_id === penId)?.name || penId}`)
                    .join(", ")}
                </p>
              </div>

              {/* Message */}
                <div style={{height: "30px", display: "flex", alignItems: "center", justifyContent: "center", width: "100%"}}>
                  <p style={{...fonts.paragraph, ...fonts.fontSize4, color: isSucces ? colors.systemGreen : colors.systemRed}}>{message}</p>
                </div>
            </div>

            {/* Pens */}
            <div style={{ gridArea: mobile ? "1/1/2/2" : "1/2/2/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: "5px", borderBottom: mobile ? "2px solid " + colors.greyTertiary : "none", borderLeft: mobile? "none" : "2px solid " + colors.greyTertiary, paddingBottom: mobile ? "15px" : "0px"}}>
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>
                {`${t("systemControls.general.clickPen", {defaultValue: "Click every pen you want to select"})}:`}
              </p>
              <div key={activeSection?.section_id} style={{ cursor: "pointer", transition: "filter 0.3s", width: "fit-content", height: "fit-content", position: "relative"}}>
                <SECTION_BASIC
                  onPenClick={handlePenSelected}
                  sectionId={activeSection?.section_id}
                  sectionType={SectionBasicTypes.selectablePen}
                  pens={activeSection?.section_locations}
                  selectedPenIds={activePenId}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "5px"}}>
                <p style={{ ...fonts.paragraph, ...fonts.fontSize4,  color: colors.greyDarkPrimary }}>
                  {`${t("systemControls.general.orSelectAll", { defaultValue: "Or select all" })}:`}
                </p>
                <CheckBox
                  checkStyle={CheckStyles.grey}
                  onChange={() => {
                    if (isAllSelected) {
                      setActivePenId([]);
                    } else {
                      setActivePenId(allPenIds ?? []);
                    }
                    setIsAllSelected(!isAllSelected);
                  }}
                  isChecked={isAllSelected}
                />
              </div>
            </div>
          </div>
        }
      />
    )
  );
}